import { faCog, faEdit, faInfoCircle, faLock, faPlus, faSave, faSpinner, faTrash } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, Card, Checkbox, Grid, Modal, Stack, Text, TextInput } from "@mantine/core";
import React, { useCallback, useState } from "react";
import { IAssociation, IAssociationConfig } from "../../../types/association/Association";
import { IGameLabel } from "../../../types/game/Game";
import PersonMultiSelect from "./PersonMultiSelect";
import VenueSelector from "./VenueSelector";

interface LabelManagerProps {
  labels: IGameLabel[];
  onChange: (labels: IGameLabel[]) => void;
  disabled?: boolean;
}

const generateRandomHexColor = () => `#${Math.floor(Math.random() * 0xffffff).toString(16).padStart(6, "0")}`;

const pickDistinctColor = (existing: string[]) => {
  const palette = existing.map(color => color?.toLowerCase()).filter(Boolean) as string[];

  for (let attempt = 0; attempt < 10; attempt += 1) {
    const candidate = generateRandomHexColor();
    if (!palette.includes(candidate.toLowerCase())) {
      return candidate;
    }
  }

  return generateRandomHexColor();
};

const LabelManager: React.FunctionComponent<LabelManagerProps> = ({ labels, onChange, disabled }) => {
  const [newLabelName, setNewLabelName] = useState("");
  const [newLabelColor, setNewLabelColor] = useState(() => pickDistinctColor(labels.map(label => label.color)));

  const handleAddLabel = () => {
    if (!newLabelName.trim() || disabled) return;

    const newLabel: IGameLabel = {
      _id: `temp_${Date.now()}`, // Temporary ID, will be replaced by backend
      name: newLabelName.trim(),
      color: newLabelColor,
      includeOnPublishedRoster: false,
    };

    const updatedLabels = [...labels, newLabel];
    onChange(updatedLabels);
    setNewLabelName("");
    setNewLabelColor(pickDistinctColor(updatedLabels.map(label => label.color)));
  };

  const handleDeleteLabel = (labelId: string) => {
    onChange(labels.filter(label => label._id !== labelId));
  };

  const handleUpdateLabel = (labelId: string, updates: Partial<IGameLabel>) => {
    onChange(labels.map(label =>
      label._id === labelId ? { ...label, ...updates } : label
    ));
  };

  return (
    <div>
      <Text size="sm" fw={500} className="mb-3">
        <strong>Game Labels</strong>
      </Text>

      {/* Existing labels */}
      {labels.length > 0 && (
        <div className="mb-3">
          <small className="text-muted">Current Labels:</small>
          <div className="d-flex flex-wrap gap-2 mt-2">
            {labels.map(label => (
              <div key={label._id} className="d-flex flex-column gap-1 p-2 border rounded" style={{ backgroundColor: `${label.color}20`, minWidth: '260px' }}>
                <div className="d-flex align-items-center gap-2">
                  <div
                    className="rounded-circle"
                    style={{
                      width: '12px',
                      height: '12px',
                      backgroundColor: label.color,
                      border: '1px solid #ccc'
                    }}
                  />
                  <TextInput
                    size="xs"
                    value={label.name}
                    onChange={(e) => handleUpdateLabel(label._id, { name: e.target.value })}
                    disabled={disabled}
                    style={{ width: '120px' }}
                  />
                  <input
                    type="color"
                    value={label.color}
                    onChange={(e) => handleUpdateLabel(label._id, { color: e.target.value })}
                    disabled={disabled}
                    style={{ width: '30px', height: '24px', border: 'none', cursor: disabled ? 'not-allowed' : 'pointer' }}
                  />
                  {!disabled && (
                    <Button
                      size="xs"
                      color="red"
                      variant="subtle"
                      onClick={() => handleDeleteLabel(label._id)}
                    >
                      <FontAwesomeIcon icon={faTrash} />
                    </Button>
                  )}
                </div>
                <div className="d-flex align-items-center gap-2 small">
                  <Checkbox
                    size="xs"
                    label={<span style={{ fontSize: '0.7rem' }}>Show on Published Roster</span>}
                    checked={!!label.includeOnPublishedRoster}
                    onChange={(e) => handleUpdateLabel(label._id, { includeOnPublishedRoster: e.currentTarget.checked })}
                    disabled={disabled}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Add new label */}
      {!disabled && (
        <div className="d-flex align-items-end gap-2">
          <div>
            <Text size="xs" className="mb-1">Label Name</Text>
            <TextInput
              size="xs"
              placeholder="Enter label name"
              value={newLabelName}
              onChange={(e) => setNewLabelName(e.target.value)}
              style={{ width: '150px' }}
            />
          </div>
          <div>
            <Text size="xs" className="mb-1">Color</Text>
            <input
              type="color"
              value={newLabelColor}
              onChange={(e) => setNewLabelColor(e.target.value)}
              style={{ width: '50px', height: '30px', border: '1px solid #ccc', borderRadius: '4px' }}
            />
          </div>
          <Button
            size="xs"
            onClick={handleAddLabel}
            disabled={!newLabelName.trim()}
          >
            <FontAwesomeIcon icon={faPlus} className="me-1" />
            Add Label
          </Button>
        </div>
      )}

      <div className="text-muted small mt-2">
        <FontAwesomeIcon icon={faInfoCircle} className="me-1" />
        Labels can be applied to games by shift admins to help organize and categorize games. Use the toggle to choose which labels appear on the published roster.
      </div>
    </div>
  );
};

interface AssociationConfigEditorProps {
  association: IAssociation;
  onSave: (configUpdates: Partial<IAssociationConfig>) => Promise<void>;
}

interface FieldConfig {
  label: string;
  type: 'text' | 'number' | 'boolean' | 'personList' | 'venue' | 'labelList';
  helpText: string;
  disabled?: boolean;
  group: 'general' | 'notifications' | 'playhq' | 'labels' | 'advanced';
}

interface FieldGroup {
  id: string;
  title: string;
  description: string;
  icon: string;
}

// Define field groups for better organization
const FIELD_GROUPS: Record<string, FieldGroup> = {
  general: {
    id: 'general',
    title: 'General Settings',
    description: 'Basic association configuration and preferences',
    icon: 'fas fa-cog'
  },
  notifications: {
    id: 'notifications',
    title: 'Notifications',
    description: 'Configure who gets notified about important events',
    icon: 'fas fa-bell'
  },
  playhq: {
    id: 'playhq',
    title: 'PlayHQ Integration',
    description: 'Settings for PlayHQ data import and synchronization',
    icon: 'fas fa-sync-alt'
  },
  labels: {
    id: 'labels',
    title: 'Game Labels',
    description: 'Define labels that can be applied to games for organization',
    icon: 'fas fa-tags'
  },
  advanced: {
    id: 'advanced',
    title: 'Advanced Settings',
    description: 'Additional configuration options',
    icon: 'fas fa-tools'
  }
};

// Define all available config fields - editable by default unless disabled
const EDITABLE_FIELDS: Record<string, FieldConfig> = {
  tz: { label: "Timezone", type: "text", helpText: "Association timezone (e.g., Australia/Melbourne)", group: "general" },
  refereesCanSeeFullRoster: { label: "Referees Can See Full Roster", type: "boolean", helpText: "Allow referees to view complete roster information", group: "general" },
  homeVenue: { label: "Home Venue", type: "venue", helpText: "Default home venue for the association", group: "general" },
  maxTimeToRejectGames: { label: "Max Time to Reject Games (hours)", type: "number", helpText: "Maximum hours before game start that referees can reject", group: "advanced" },
  requireRefereesToCompleteGames: { label: "Require Referees to Complete Games", type: "boolean", helpText: "Referees must mark games as complete", group: "advanced" },
  paymentDisputeLink: { label: "Payment Dispute Link", type: "text", helpText: "URL for payment dispute resolution", group: "advanced" },
  "notifications.whenGamesAreDeleted": { label: "Game Deletion Notifications", type: "personList", helpText: "People to notify when games are deleted", group: "notifications" },
  "playHQ.associationId": { label: "PlayHQ Association ID", type: "text", helpText: "PlayHQ integration association identifier", disabled: true, group: "playhq" },
  "playHQ.tenant": { label: "PlayHQ Tenant", type: "text", helpText: "PlayHQ tenant identifier", disabled: true, group: "playhq" },
  "playHQ.ignoreMissingAssociations": { label: "PlayHQ Ignore Missing Associations", type: "boolean", helpText: "Skip validation for missing associations in PlayHQ", group: "playhq" },
  "playHQ.defaultGameLength": { label: "PlayHQ Default Game Length (minutes)", type: "number", helpText: "Default game duration for PlayHQ imports", group: "playhq" },
  labels: { label: "Game Labels", type: "labelList", helpText: "Define labels that can be applied to games", group: "labels" }
} as const;

const LOCKED_FIELDS = ["billing", "availabilities"];

const AssociationConfigEditor: React.FunctionComponent<AssociationConfigEditorProps> = ({
  association,
  onSave,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editedConfig, setEditedConfig] = useState<Partial<IAssociationConfig>>({});
  const [saving, setSaving] = useState(false);

  const handleStartEdit = useCallback(() => {
    // SECURITY: Only initialize with validated editable fields from the current config
    const editableConfig: Partial<IAssociationConfig> = {};

    Object.keys(EDITABLE_FIELDS).forEach(fieldKey => {
      // SECURITY: Validate field key against allowed list
      if (!(fieldKey in EDITABLE_FIELDS)) {
        console.warn(`Attempted to access non-editable field: ${fieldKey}`);
        return;
      }

      if (fieldKey.includes('.')) {
        const [parent, child] = fieldKey.split('.');

        // SECURITY: Validate parent and child keys
        if (!parent || !child || parent.length === 0 || child.length === 0) {
          console.warn(`Invalid nested field key: ${fieldKey}`);
          return;
        }

        const parentConfig = association.config?.[parent as keyof IAssociationConfig];
        if (parentConfig && typeof parentConfig === 'object' && child in parentConfig) {
          if (!editableConfig[parent as keyof IAssociationConfig]) {
            (editableConfig as any)[parent] = {};
          }
          ((editableConfig as any)[parent])[child] = (parentConfig as any)[child];
        }
      } else {
        // SECURITY: Direct field access with validation
        if (fieldKey in association.config) {
          (editableConfig as any)[fieldKey] = association.config[fieldKey as keyof IAssociationConfig];
        }
      }
    });

    setEditedConfig(editableConfig);
    setIsEditing(true);
  }, [association.config]);

  const handleCancelEdit = useCallback(() => {
    setEditedConfig({});
    setIsEditing(false);
  }, []);

  const handleSave = useCallback(async () => {
    try {
      setSaving(true);

      // SECURITY: Only send fields that have actually changed and are validated
      const changedFields: Partial<IAssociationConfig> = {};

      Object.entries(EDITABLE_FIELDS).forEach(([fieldKey, fieldConfig]) => {
        // SECURITY: Skip disabled fields
        if (fieldConfig.disabled) {
          return;
        }

        // SECURITY: Validate field key against allowed list
        if (!(fieldKey in EDITABLE_FIELDS)) {
          console.warn(`Attempted to save non-editable field: ${fieldKey}`);
          return;
        }

        if (fieldKey.includes('.')) {
          const [parent, child] = fieldKey.split('.');

          // SECURITY: Validate parent and child keys
          if (!parent || !child || parent.length === 0 || child.length === 0) {
            console.warn(`Invalid nested field key during save: ${fieldKey}`);
            return;
          }

          const originalValue = (association.config as any)?.[parent]?.[child];
          const newValue = (editedConfig as any)?.[parent]?.[child];

          // SECURITY: Use proper comparison instead of JSON.stringify
          if (originalValue !== newValue) {
            if (!(changedFields as any)[parent]) {
              (changedFields as any)[parent] = {};
            }
            ((changedFields as any)[parent])[child] = newValue;
          }
        } else {
          const originalValue = (association.config as any)[fieldKey];
          const newValue = (editedConfig as any)[fieldKey];

          // SECURITY: Use proper comparison instead of JSON.stringify
          if (originalValue !== newValue) {
            (changedFields as any)[fieldKey] = newValue;
          }
        }
      });

      // Only proceed if there are actual changes
      if (Object.keys(changedFields).length > 0) {
        // SECURITY: Log config changes for audit trail
        console.info('Association config changes:', {
          associationId: association._id,
          changes: Object.keys(changedFields),
          timestamp: new Date().toISOString()
        });

        await onSave(changedFields);
      }

      setIsEditing(false);
      setEditedConfig({});
    } catch (error) {
      console.error("Failed to save config:", error);
      // Error handling will be done by parent component
    } finally {
      setSaving(false);
    }
  }, [editedConfig, onSave, association.config, association._id]);

  const handleFieldChange = useCallback((fieldPath: string, value: unknown) => {
    // SECURITY: Validate field path against allowed fields
    if (!(fieldPath in EDITABLE_FIELDS)) {
      console.warn(`Attempted to change non-editable field: ${fieldPath}`);
      return;
    }

    setEditedConfig(prev => {
      const newConfig = { ...prev };

      // Handle nested field paths like "notifications.whenGamesAreDeleted"
      if (fieldPath.includes('.')) {
        const [parent, child] = fieldPath.split('.');

        // SECURITY: Validate parent and child keys
        if (!parent || !child || parent.length === 0 || child.length === 0) {
          console.warn(`Invalid nested field path: ${fieldPath}`);
          return prev;
        }

        if (!(newConfig as any)[parent]) {
          (newConfig as any)[parent] = {};
        }
        (newConfig as any)[parent] = { ...(newConfig as any)[parent], [child]: value };
      } else {
        (newConfig as any)[fieldPath] = value;
      }

      return newConfig;
    });
  }, []);

  const getFieldValue = useCallback((fieldPath: string, config: IAssociationConfig) => {
    if (fieldPath.includes('.')) {
      const [parent, child] = fieldPath.split('.');
      return (config as any)[parent]?.[child];
    }
    return (config as any)[fieldPath];
  }, []);

  const renderFieldEditor = useCallback((fieldKey: string, fieldConfig: FieldConfig) => {
    const currentValue = getFieldValue(fieldKey, editedConfig as IAssociationConfig);
    const originalValue = getFieldValue(fieldKey, association.config);
    const isDisabled = fieldConfig.disabled || false;
    const isModified = JSON.stringify(currentValue) !== JSON.stringify(originalValue);

    switch (fieldConfig.type) {
      case "boolean":
        return (
          <Stack
            gap="xs"
            key={fieldKey}
            className={`${isModified ? 'border-start border-warning border-3 ps-3' : ''}`}
          >
            <Checkbox
              id={fieldKey}
              checked={Boolean(currentValue)}
              onChange={(event) => handleFieldChange(fieldKey, event.currentTarget.checked)}
              disabled={isDisabled}
              label={(
                <span
                  className={`${isDisabled ? 'text-muted' : ''}`}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    fontWeight: 600,
                  }}
                >
                  {fieldConfig.label}
                  {isDisabled && (
                    <FontAwesomeIcon
                      icon={faLock}
                      className="text-muted"
                      title="This field cannot be modified"
                    />
                  )}
                  {isModified && (
                    <span className="badge bg-warning" style={{ fontSize: '0.7em' }}>
                      Modified
                    </span>
                  )}
                </span>
              )}
              description={fieldConfig.helpText ? (
                <div className="text-muted small">
                  <FontAwesomeIcon icon={faInfoCircle} className="me-1" aria-hidden="true" />
                  {fieldConfig.helpText}
                </div>
              ) : undefined}
            />
          </Stack>
        );

      case "number":
        return (
          <Stack gap="sm" key={fieldKey} className={`${isModified ? 'border-start border-warning border-3 ps-3' : ''}`}>
            <label htmlFor={fieldKey} className={`fw-bold ${isDisabled ? 'text-muted' : ''}`}>
              <strong>{fieldConfig.label}</strong>
              {isDisabled && <FontAwesomeIcon icon={faLock} className="ms-2 text-muted" title="This field cannot be modified" />}
              {isModified && <span className="badge bg-warning ms-2" style={{ fontSize: '0.7em' }}>Modified</span>}
            </label>
            <TextInput
              type="number"
              id={fieldKey}
              value={currentValue || ""}
              onChange={(e) => handleFieldChange(fieldKey, parseInt(e.target.value) || 0)}
              disabled={isDisabled}
              className={isModified ? 'border-warning' : ''}
            />
            {fieldConfig.helpText && (
              <div className="text-muted small mt-1">
                <FontAwesomeIcon icon={faInfoCircle} className="me-1" />
                {fieldConfig.helpText}
              </div>
            )}
          </Stack>
        );

      case "personList":
        return (
          <div key={fieldKey} className={`${isModified ? 'border-start border-warning border-3 ps-3' : ''}`}>
            <PersonMultiSelect
              label={`${fieldConfig.label}${isModified ? '' : ''}`}
              value={currentValue || []}
              onChange={(personIds) => handleFieldChange(fieldKey, personIds)}
              associationId={association._id}
              helpText={fieldConfig.helpText}
              disabled={isDisabled}
            />
            {isModified && (
              <div className="mb-2">
                <span className="badge bg-warning" style={{ fontSize: '0.7em' }}>Modified</span>
              </div>
            )}
          </div>
        );

      case "venue":
        return (
          <div key={fieldKey} className={`${isModified ? 'border-start border-warning border-3 ps-3' : ''}`}>
            <VenueSelector
              label={fieldConfig.label}
              value={currentValue || null}
              onChange={(venueId) => handleFieldChange(fieldKey, venueId)}
              helpText={fieldConfig.helpText}
              disabled={isDisabled}
            />
            {isModified && (
              <div className="mb-2">
                <span className="badge bg-warning" style={{ fontSize: '0.7em' }}>Modified</span>
              </div>
            )}
          </div>
        );

      case "labelList":
        return (
          <div key={fieldKey} className={`${isModified ? 'border-start border-warning border-3 ps-3' : ''}`}>
            <LabelManager
              labels={currentValue || []}
              onChange={(labels) => handleFieldChange(fieldKey, labels)}
              disabled={isDisabled}
            />
            {isModified && (
              <div className="mb-2">
                <span className="badge bg-warning" style={{ fontSize: '0.7em' }}>Modified</span>
              </div>
            )}
          </div>
        );

      default: // text
        return (
          <Stack gap="sm" key={fieldKey} className={`${isModified ? 'border-start border-warning border-3 ps-3' : ''}`}>
            <label htmlFor={fieldKey} className={`fw-bold ${isDisabled ? 'text-muted' : ''}`}>
              <strong>{fieldConfig.label}</strong>
              {isDisabled && <FontAwesomeIcon icon={faLock} className="ms-2 text-muted" title="This field cannot be modified" />}
              {isModified && <span className="badge bg-warning ms-2" style={{ fontSize: '0.7em' }}>Modified</span>}
            </label>
            <TextInput
              type="text"
              id={fieldKey}
              value={currentValue || ""}
              onChange={(e) => handleFieldChange(fieldKey, e.target.value)}
              disabled={isDisabled}
              className={isModified ? 'border-warning' : ''}
            />
            {fieldConfig.helpText && (
              <div className="text-muted small mt-1">
                <FontAwesomeIcon icon={faInfoCircle} className="me-1" />
                {fieldConfig.helpText}
              </div>
            )}
          </Stack>
        );
    }
  }, [editedConfig, handleFieldChange, association._id, getFieldValue]);

  const renderSectionHeader = useCallback((group: FieldGroup) => (
    <div className="p-3 p-md-4 border-bottom" style={{ backgroundColor: 'var(--color-bg-disabled-card)' }}>
      <div className="d-flex align-items-center">
        <FontAwesomeIcon icon={faCog} className="me-3" style={{ color: 'var(--color-brand-dark)' }} aria-hidden="true" />
        <div>
          <h5 className="mb-1">{group.title}</h5>
          <small className="text-muted">{group.description}</small>
        </div>
      </div>
    </div>
  ), []);

  const renderFieldGroup = useCallback((groupId: string) => {
    const fieldsInGroup = Object.entries(EDITABLE_FIELDS).filter(([, config]) => config.group === groupId);

    if (fieldsInGroup.length === 0) return null;

    return fieldsInGroup.map(([key, config]) => (
      <div key={key} className="mb-3">
        {renderFieldEditor(key, config)}
      </div>
    ));
  }, [renderFieldEditor]);

  const renderDisplayValue = useCallback((fieldKey: string, fieldConfig: FieldConfig) => {
    const value = getFieldValue(fieldKey, association.config);

    if (fieldConfig.type === "boolean") {
      return value ? "Yes" : "No";
    }

    if (fieldConfig.type === "personList") {
      return value && value.length > 0 ? `${value.length} person(s) selected` : "None selected";
    }

    if (fieldConfig.type === "venue") {
      return value ? "Venue selected" : "No venue selected";
    }

    if (fieldConfig.type === "labelList") {
      return value && value.length > 0 ? `${value.length} label(s) defined` : "No labels defined";
    }

    return value || "Not set";
  }, [association.config, getFieldValue]);

  if (isEditing) {
    return (
      <Modal
        opened={true}
        onClose={handleCancelEdit}
        size="xl"
        aria-labelledby="config-editor-title"
        aria-describedby="config-editor-description"
        title={
          <div className="d-flex align-items-center">
            <FontAwesomeIcon icon={faCog} className="me-2" style={{ color: 'var(--color-brand-dark)' }} aria-hidden="true" />
            <span id="config-editor-title">Edit Association Settings</span>
          </div>
        }
      >
        <div className="p-0" style={{ maxHeight: 'calc(100vh - 200px)', overflowY: 'auto' }}>
          <div id="config-editor-description" className="visually-hidden">
            Configure settings for your association including general settings, notifications, PlayHQ integration, and advanced options.
          </div>
          <Stack gap="md" role="form" aria-label="Association configuration settings">
            {Object.values(FIELD_GROUPS).map(group => (
              <div key={group.id} className="mb-4">
                {renderSectionHeader(group)}
                <div className="p-3 p-md-4 ps-4 ps-md-5">
                  <Grid>
                    <Grid.Col span={{ base: 12, md: 10, lg: 8, xl: 6 }}>
                      {renderFieldGroup(group.id)}
                    </Grid.Col>
                  </Grid>
                </div>
              </div>
            ))}
          </Stack>
        </div>
        <div className="d-flex flex-column flex-md-row justify-content-between" style={{ marginTop: '1rem' }}>
          <div className="mb-2 mb-md-0">
            <small className="text-muted">
              <FontAwesomeIcon icon={faInfoCircle} className="me-1" aria-hidden="true" />
              Fields with <FontAwesomeIcon icon={faLock} className="text-muted" aria-hidden="true" /> are locked and cannot be modified
            </small>
          </div>
          <div className="d-flex gap-2">
            <div className="visually-hidden" aria-live="polite" aria-atomic="true">
              {saving ? "Saving association settings..." : ""}
            </div>
            <Button color="gray" onClick={handleCancelEdit} disabled={saving} className="flex-fill flex-md-grow-0">
              Cancel
            </Button>
            <Button
              color="blue"
              onClick={handleSave}
              disabled={saving}
              aria-describedby={saving ? "save-status" : undefined}
              className="flex-fill flex-md-grow-0"
            >
              <FontAwesomeIcon icon={saving ? faSpinner : faSave} spin={saving} className="me-1" aria-hidden="true" />
              {saving ? "Saving..." : "Save Changes"}
            </Button>
          </div>
        </div>
      </Modal>
    );
  }

  return (
    <Card className="mb-4">
      <Card.Section withBorder p="md" className="d-flex justify-content-between align-items-center">
        <h5 className="mb-0">
          <FontAwesomeIcon icon={faCog} className="me-2" />
          Association Settings
        </h5>
        <Button color="blue" size="sm" onClick={handleStartEdit}>
          <FontAwesomeIcon icon={faEdit} className="me-1" />
          Edit Settings
        </Button>
      </Card.Section>
      <div style={{ padding: "1rem" }}>
        <Grid>
          <Grid.Col span={{ base: 12, md: 6 }}>
            <h6 className="text-muted mb-3">General Settings</h6>
            {Object.entries(EDITABLE_FIELDS)
              .filter(([key, config]) => !key.includes('.') && config.group === 'general')
              .map(([key, config]) => (
                <div key={key} className="mb-2">
                  <strong>{config.label}:</strong>{" "}
                  <span className="text-muted">{renderDisplayValue(key, config)}</span>
                </div>
              ))}

            <h6 className="text-muted mb-3 mt-4">Game Labels</h6>
            {Object.entries(EDITABLE_FIELDS)
              .filter(([key, config]) => config.group === 'labels')
              .map(([key, config]) => (
                <div key={key} className="mb-2">
                  <strong>{config.label}:</strong>{" "}
                  <span className="text-muted">{renderDisplayValue(key, config)}</span>
                </div>
              ))}
          </Grid.Col>
          <Grid.Col span={{ base: 12, md: 6 }}>
            <h6 className="text-muted mb-3">Notifications & Integrations</h6>
            {Object.entries(EDITABLE_FIELDS)
              .filter(([key, config]) => (key.includes('.') || config.group === 'playhq' || config.group === 'notifications' || config.group === 'advanced'))
              .map(([key, config]) => (
                <div key={key} className="mb-2">
                  <strong>{config.label}:</strong>{" "}
                  <span className="text-muted">{renderDisplayValue(key, config)}</span>
                </div>
              ))}
          </Grid.Col>
        </Grid>

        {LOCKED_FIELDS.length > 0 && (
          <div className="mt-4 p-3 rounded" style={{ backgroundColor: 'var(--color-bg-disabled-card)' }}>
            <small className="text-muted">
              <FontAwesomeIcon icon={faLock} className="me-1" />
              Some settings (billing, availabilities) can only be modified by system administrators.
            </small>
          </div>
        )}
      </div>
    </Card>
  );
};

export default AssociationConfigEditor;
